import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CreditCardIcon, CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import TimchehLoader from '../components/TimchehLoader';

interface PaymentPageProps {}

const PaymentPage: React.FC<PaymentPageProps> = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [paymentStatus, setPaymentStatus] = useState<'pending' | 'processing' | 'success' | 'failed'>('pending');
  const [paymentData, setPaymentData] = useState<any>(null);

  // Get payment details from URL params
  const promotionPackageId = searchParams.get('packageId');
  const amount = searchParams.get('amount');
  const returnUrl = searchParams.get('returnUrl') || '/post-ad';
  const packageName = searchParams.get('packageName') || 'بسته ترفیع';

  useEffect(() => {
    // Initialize payment data
    if (promotionPackageId && amount) {
      setPaymentData({
        packageId: promotionPackageId,
        amount: parseFloat(amount),
        packageName: packageName
      });
    } else {
      // No payment data, redirect back
      toast.error('اطلاعات پرداخت یافت نشد');
      navigate(returnUrl);
    }
  }, [promotionPackageId, amount, packageName, returnUrl, navigate]);

  const handlePayment = async (method: 'credit_card' | 'paypal' | 'stripe') => {
    setPaymentStatus('processing');
    
    try {
      // Create payment session with actual payment service
      const response = await fetch('/api/Payment/create-session', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          promotionPackageId: promotionPackageId,
          amount: paymentData.amount,
          currency: 'CAD',
          method: method,
          returnUrl: `${window.location.origin}/payment-success`
        })
      });

      if (!response.ok) {
        throw new Error('Failed to create payment session');
      }

      const { sessionUrl } = await response.json();
      
      // Redirect to payment gateway
      window.location.href = sessionUrl;
      
    } catch (error) {
      console.error('Payment error:', error);
      setPaymentStatus('failed');
      toast.error('خطا در شروع فرآیند پرداخت');
    }
  };

  const handleCancel = () => {
    navigate(`${returnUrl}?paymentStatus=cancelled`);
  };

  if (!paymentData) {
    return (
      <TimchehLoader 
        fullScreen={true}
        size="large"
        message="در حال بارگذاری اطلاعات پرداخت..."
      />
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8" dir="rtl">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          {/* Header */}
          <div className="bg-primary-600 text-white px-6 py-4">
            <h1 className="text-2xl font-semibold flex items-center">
              <CreditCardIcon className="w-6 h-6 mr-2" />
              پرداخت بسته ترفیع
            </h1>
          </div>

          <div className="p-6">
            {paymentStatus === 'pending' && (
              <>
                {/* Payment Summary */}
                <div className="mb-8 p-4 bg-gray-50 rounded-lg">
                  <h2 className="text-lg font-semibold mb-4">خلاصه پرداخت</h2>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span>نام بسته:</span>
                      <span className="font-medium">{paymentData.packageName}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>مبلغ:</span>
                      <span className="font-semibold text-lg">${paymentData.amount.toFixed(2)} CAD</span>
                    </div>
                  </div>
                </div>

                {/* Payment Methods */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold">روش پرداخت را انتخاب کنید</h3>
                  
                  <button
                    onClick={() => handlePayment('credit_card')}
                    className="w-full p-4 border border-gray-300 rounded-lg hover:border-primary-500 transition-colors text-right"
                  >
                    <div className="flex items-center">
                      <CreditCardIcon className="w-6 h-6 ml-3 text-primary-600" />
                      <div>
                        <div className="font-medium">کارت اعتباری</div>
                        <div className="text-sm text-gray-500">Visa, Mastercard, Amex</div>
                      </div>
                    </div>
                  </button>

                  <button
                    onClick={() => handlePayment('paypal')}
                    className="w-full p-4 border border-gray-300 rounded-lg hover:border-primary-500 transition-colors text-right"
                  >
                    <div className="flex items-center">
                      <div className="w-6 h-6 ml-3 bg-blue-500 rounded text-white text-xs flex items-center justify-center font-semibold">
                        PP
                      </div>
                      <div>
                        <div className="font-medium">PayPal</div>
                        <div className="text-sm text-gray-500">پرداخت امن از طریق PayPal</div>
                      </div>
                    </div>
                  </button>

                  <button
                    onClick={() => handlePayment('stripe')}
                    className="w-full p-4 border border-gray-300 rounded-lg hover:border-primary-500 transition-colors text-right"
                  >
                    <div className="flex items-center">
                      <div className="w-6 h-6 ml-3 bg-purple-500 rounded text-white text-xs flex items-center justify-center font-semibold">
                        S
                      </div>
                      <div>
                        <div className="font-medium">Stripe</div>
                        <div className="text-sm text-gray-500">پرداخت امن از طریق Stripe</div>
                      </div>
                    </div>
                  </button>
                </div>

                {/* Cancel Button */}
                <div className="mt-8 text-center">
                  <button
                    onClick={handleCancel}
                    className="text-gray-600 hover:text-gray-800 underline"
                  >
                    انصراف و بازگشت
                  </button>
                </div>
              </>
            )}

            {paymentStatus === 'processing' && (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary-600 mx-auto mb-4"></div>
                <h2 className="text-xl font-semibold mb-2">در حال پردازش پرداخت...</h2>
                <p className="text-gray-600">لطفا منتظر بمانید</p>
              </div>
            )}

            {paymentStatus === 'success' && (
              <div className="text-center py-8">
                <CheckCircleIcon className="w-16 h-16 text-green-500 mx-auto mb-4" />
                <h2 className="text-xl font-semibold text-green-600 mb-2">پرداخت موفق!</h2>
                <p className="text-gray-600 mb-4">بسته ترفیع شما با موفقیت فعال شد.</p>
                <div className="text-sm text-gray-500">در حال بازگشت...</div>
              </div>
            )}

            {paymentStatus === 'failed' && (
              <div className="text-center py-8">
                <XCircleIcon className="w-16 h-16 text-red-500 mx-auto mb-4" />
                <h2 className="text-xl font-semibold text-red-600 mb-2">پرداخت ناموفق</h2>
                <p className="text-gray-600 mb-6">متأسفانه پرداخت شما با خطا مواجه شد.</p>
                <div className="space-y-4">
                  <button
                    onClick={() => setPaymentStatus('pending')}
                    className="bg-primary-600 text-white px-6 py-2 rounded-lg hover:bg-primary-700 transition-colors"
                  >
                    تلاش مجدد
                  </button>
                  <button
                    onClick={handleCancel}
                    className="block mx-auto text-gray-600 hover:text-gray-800 underline"
                  >
                    انصراف و بازگشت
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentPage; 